import i18n from '@libs/utils/translations'
import type {
  AxiosInstance,
  AxiosRequestConfig,
  CreateAxiosDefaults,
  InternalAxiosRequestConfig
} from 'axios'
import axios from 'axios'

import type { ApiErrorResponse } from '../../types'

import type {
  HttpServiceEndpoints,
  SafeURLPath
} from './interfaces/HttpClient.interface'

type Headers = Record<string, string>

export class HttpModel {
  private httpClientInstance: AxiosInstance

  public base: HttpServiceEndpoints

  public static contentTypes = {
    multipartFormData: { 'Content-Type': 'multipart/form-data' },
    json: { 'Content-Type': 'application/json' }
  }

  constructor(
    axiosOptions: CreateAxiosDefaults = {},
    defaultHeaders: Headers = {}
  ) {
    this.httpClientInstance = axios.create(axiosOptions)
    this.updateHeaders(defaultHeaders)

    this.setupInterceptors()

    this.base = this.createServiceEndpoints('')
  }

  public updateHeaders(headers: Headers) {
    Object.assign(this.httpClientInstance.defaults.headers.common, headers)
  }

  public createServiceEndpoints(basePath: string): HttpServiceEndpoints {
    return {
      get: <T>(url: SafeURLPath, config?: AxiosRequestConfig<any>) =>
        this.httpClientInstance.get<T>(`${basePath}${url}`, config),
      post: <T>(
        url: SafeURLPath,
        data?: any,
        config?: AxiosRequestConfig<any>
      ) => this.httpClientInstance.post<T>(`${basePath}${url}`, data, config),
      put: <T>(
        url: SafeURLPath,
        data?: any,
        config?: AxiosRequestConfig<any>
      ) => this.httpClientInstance.put<T>(`${basePath}${url}`, data, config),
      patch: <T>(
        url: SafeURLPath,
        data?: any,
        config?: AxiosRequestConfig<any>
      ) => this.httpClientInstance.patch<T>(`${basePath}${url}`, data, config),
      delete: <T>(url: SafeURLPath, config?: AxiosRequestConfig<any>) =>
        this.httpClientInstance.delete<T>(`${basePath}${url}`, config)
    }
  }

  get httpInstance() {
    return this.httpClientInstance
  }

  protected getRequestInterceptors(): Array<
    (
      value: InternalAxiosRequestConfig<any>
    ) =>
      | InternalAxiosRequestConfig<any>
      | Promise<InternalAxiosRequestConfig<any>>
  > {
    return []
  }

  protected getResponseInterceptors(): Array<(error: any) => any> {
    return []
  }

  private setupInterceptors(): void {
    this.getRequestInterceptors().forEach(interceptor => {
      this.httpClientInstance.interceptors.request.use(interceptor)
    })

    this.httpClientInstance.interceptors.response.use(
      response => response,
      error => {
        this.getResponseInterceptors().forEach(interceptor =>
          interceptor(error)
        )
        const defaultError = {
          message: i18n.t('common.app:message.error.internalErrorRetry'),
          target: null,
          code: null,
          details: []
        }

        const formattedError: ApiErrorResponse = {
          error: {
            ...defaultError,
            ...(error.response?.data?.message ? error.response.data : {})
          }
        }

        return Promise.reject(formattedError)
      }
    )
  }
}
